/**
 * Floorplan Persona Configuration
 *
 * Specialized persona for floor plan generation with two modes:
 * - Floor Select: Selection tool for choosing floor boundaries in 2D view
 * - Floorplan: Floor plan generation tool with 3D view
 */

import { StructureKind } from 'shared-types';
import { IPersonaConfig, ToolbarElementType } from '../../types';
import { ViewModes } from '../../helpers';
import {
  AutoFloorTool,
  FloorSelectorTool,
  ManualFloorTool,
  CameraOrientationTool,
  RoomHostTool,
  ManualRoomHostTool,
  DoorWindowTool,
  SketchObjectTool,
  WallCreateTool,
  WallEditTool,
  RoomSelectionTool,
  RoomMergeTool,
  RoomTagTool,
  WallSelectionTool,
  DesignSelectionTool,
  StructureHostTool,
  ManualStructureHostTool,
  StructureSelectionTool,
  fullFloorSlabIsolationStrategy,
} from '../../tools';
import { FloorplanPersona } from '../../personas/implementations/FloorplanPersona';

export const FloorplanPersonaConfig: IPersonaConfig = {
  name: 'floorplan',
  priority: 90,
  exclusive: false,
  factory: (viewer, config, viewManager) => new FloorplanPersona(viewer, config, viewManager),

  // Persona default: X-Ray 2D top-down view (mode-specific views override this)
  defaultViewConfig: {
    views: [ViewModes.XRAY_2D],
    defaultViewId: 'xray_2d',
  },

  modes: [
    {
      id: 'floor_select',
      name: 'floor select',
      toolbarElements: [
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'floor_selector',
            factory: (viewer, persona) => new FloorSelectorTool(viewer, persona),
            defaultEnabled: true,
            exclusive: true,
            icon: 'select',
            name: 'floor selector',
          },
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'camera_orientation',
            factory: (viewer, persona) => new CameraOrientationTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'undo-dot',
            name: 'camera orientation',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'auto_floor',
            factory: (viewer, persona) => new AutoFloorTool(viewer, persona),
            defaultEnabled: false,
            exclusive: true,
            icon: 'Floor-detection-automatisch',
            name: 'auto floor',
          },
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'manual_floor',
            factory: (viewer, persona) => new ManualFloorTool(viewer, persona),
            defaultEnabled: false,
            exclusive: true,
            icon: 'Floor-detection-halfautomatisch',
            name: 'manual floor',
          },
        },
        // Spacer before undo/redo
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Undo action
        {
          type: ToolbarElementType.ACTION,
          id: 'undo',
          icon: 'undo',
          tooltip: 'undo',
        },
        // Redo action
        {
          type: ToolbarElementType.ACTION,
          id: 'redo',
          icon: 'redo',
          tooltip: 'redo',
        },
      ],
    },
    {
      id: 'floorplan',
      name: 'floorplan',
      toolbarElements: [
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'room_selection',
            factory: (viewer, persona) => new RoomSelectionTool(viewer, persona),
            defaultEnabled: true,
            exclusive: true,
            icon: 'select',
            name: 'room selection',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'camera_orientation',
            factory: (viewer, persona) => new CameraOrientationTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'undo-dot',
            name: 'camera orientation',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Magic room — LineTool subtool (auto/manual line modes via its mode
        // toggle), RoomKindAdapter, requires floor selection.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'line_detection',
            factory: (viewer, persona) => new RoomHostTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'Room-detection-automatisch',
            name: 'magic room',
            disabledTooltip: 'select a floor first',
          },
        },
        // Manual room draw — click-to-place points (MeasureEngine) on the floor
        // plane, committed via the same RoomKindAdapter.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'manual_room_draw',
            factory: (viewer, persona) => new ManualRoomHostTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'Room-detection-manueel',
            name: 'manual room draw',
            disabledTooltip: 'select a floor first',
          },
        },
        // The experimental room tools (WallTracerTool, LidarProbeRoomTool,
        // LoopTraceRoomTool) are intentionally unlisted from the toolbar.
        // Merge rooms — click adjacent rooms to build a selection; a live
        // preview shows the merged boundary and Enter commits it (the largest
        // room keeps its identity, the others are deleted).
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'room_merge',
            factory: (viewer, persona) => new RoomMergeTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'merge-rooms',
            name: 'merge rooms',
            disabledTooltip: 'select a floor first',
          },
        },
        // Spacer before undo/redo
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Undo action
        {
          type: ToolbarElementType.ACTION,
          id: 'undo',
          icon: 'undo',
          tooltip: 'undo',
        },
        // Redo action
        {
          type: ToolbarElementType.ACTION,
          id: 'redo',
          icon: 'redo',
          tooltip: 'redo',
        },
      ],
      viewConfig: {
        views: [ViewModes.PERSPECTIVE_3D_RGB, ViewModes.XRAY_2D],
        defaultViewId: 'perspective_3d_rgb',
      },
    },
    {
      id: 'wall',
      name: 'wall',
      toolbarElements: [
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'wall_selection',
            factory: (viewer, persona) => new WallSelectionTool(viewer, persona),
            defaultEnabled: true,
            exclusive: true,
            icon: 'select',
            name: 'wall selection',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'camera_orientation',
            factory: (viewer, persona) => new CameraOrientationTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'undo-dot',
            name: 'camera orientation',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Create walls — suggests a wall sketch from the floor's rooms (outer
        // wall + generated skeleton preview, Enter accepts); once a sketch
        // exists it becomes a draw-only tool (drag to draw/branch walls,
        // right-click deletes, no selection).
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'wall_create',
            factory: (viewer, persona) => new WallCreateTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'wall-create',
            name: 'create walls',
            disabledTooltip: 'select a floor first',
          },
        },
        // Edit walls — the wall sketch editor with the creation gestures
        // stripped: selection, move, thickness, curvature, delete.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'wall_edit',
            factory: (viewer, persona) => new WallEditTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true,
            exclusive: true,
            icon: 'wall-edit',
            name: 'edit walls',
            disabledTooltip: 'select a floor first',
          },
        },
        // The former separate "edit outer wall" tool is gone: since the
        // outline merge, wall_edit edits the outline ring edges on Measured
        // floors with the same handles as walls.
        // Spacer before undo/redo
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Undo action
        {
          type: ToolbarElementType.ACTION,
          id: 'undo',
          icon: 'undo',
          tooltip: 'undo',
        },
        // Redo action
        {
          type: ToolbarElementType.ACTION,
          id: 'redo',
          icon: 'redo',
          tooltip: 'redo',
        },
      ],
      viewConfig: {
        views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.PERSPECTIVE_3D_RGB],
        defaultViewId: 'perspective_3d_rgb',
      },
    },
    {
      id: 'design',
      name: 'design',
      toolbarElements: [
        {
          type: ToolbarElementType.TOOL,
          config: {
            // Keep this id: FloorplanPersona's selector-fallback list resolves
            // the mode's default selection tool by it.
            id: 'design_selection',
            factory: (viewer, persona) => new DesignSelectionTool(viewer, persona),
            defaultEnabled: true,
            exclusive: true,
            icon: 'select',
            name: 'selection',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'camera_orientation',
            factory: (viewer, persona) => new CameraOrientationTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'undo-dot',
            name: 'camera orientation',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Room annotation — creates room tags (ROOM groups): hover highlights
        // the room per the Measured/Wall-sketch switch, click drops a tag at
        // the cursor; existing tags stay draggable within this tool.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'room_tag',
            factory: (viewer, persona) => new RoomTagTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'room',
            name: 'room annotation',
            disabledTooltip: 'select a floor first',
          },
        },
        // Magic door/window — rectangle-only segmentation draw on a focused
        // wall; commits parametric OPENING_SKETCHes onto the wall sketch, so
        // it needs walls to exist first.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'door_window',
            factory: (viewer, persona) => new DoorWindowTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true,
            exclusive: true,
            icon: 'Surface-detection-automatisch',
            name: 'Magic door/window',
            disabledTooltip: 'create walls first',
            requiresWallSketch: true,
          },
        },
        // Objects — the sketch-object library (furniture & fixtures) plus the
        // doors & windows category (places OPENING_SKETCHes like the rest).
        // Same class the Layout persona hosts; registered here too until that
        // persona is dissolved.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'sketch_object',
            factory: (viewer, persona) => new SketchObjectTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true,
            exclusive: true,
            icon: 'chair',
            name: 'objects',
            disabledTooltip: 'create walls first',
            requiresWallSketch: true,
          },
        },
        // Dimensions — the same placement tool on the dimension library:
        // armed with the default dimension, two clicks measure between any
        // two points (ends snapping to wall corners / faces when there are
        // walls, so it needs none).
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'dimension',
            factory: (viewer, persona) => new SketchObjectTool(viewer, persona, 'dimensions'),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'ruler',
            name: 'dimensions',
            disabledTooltip: 'select a floor first',
          },
        },
        // Spacer before undo/redo
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Undo action
        {
          type: ToolbarElementType.ACTION,
          id: 'undo',
          icon: 'undo',
          tooltip: 'undo',
        },
        // Redo action
        {
          type: ToolbarElementType.ACTION,
          id: 'redo',
          icon: 'redo',
          tooltip: 'redo',
        },
      ],
      viewConfig: {
        views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.PERSPECTIVE_3D_RGB],
        defaultViewId: 'perspective_3d_rgb',
      },
    },
    {
      id: 'structure',
      name: 'structure',
      toolbarElements: [
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'structure_selection',
            factory: (viewer, persona) => new StructureSelectionTool(viewer, persona),
            defaultEnabled: true,
            exclusive: true,
            icon: 'select',
            name: 'structure selection',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'camera_orientation',
            factory: (viewer, persona) => new CameraOrientationTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'undo-dot',
            name: 'camera orientation',
            disabledTooltip: 'select a floor first',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Magic Beam — StructureHostTool fixed to BEAM. Detects the beam top via
        // position-map sampling and extrudes up to the room ceiling.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'beam_draw',
            factory: (viewer, persona) => new StructureHostTool(viewer, persona, StructureKind.BEAM, fullFloorSlabIsolationStrategy),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'Beam_automatisch',
            name: 'magic beam',
            disabledTooltip: 'select a floor first',
          },
        },
        // Magic Column — StructureHostTool fixed to COLUMN. Extrudes the full
        // room height (floor to ceiling).
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'column_draw',
            factory: (viewer, persona) => new StructureHostTool(viewer, persona, StructureKind.COLUMN),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'Column_automatisch',
            name: 'magic column',
            disabledTooltip: 'select a floor first',
          },
        },
        // Manual structure draw — click-to-place points (MeasureEngine) on the
        // floor plane, committed via the same StructureKindAdapter. Defaults to
        // COLUMN; a settings toggle switches it to BEAM.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'manual_structure_draw',
            factory: (viewer, persona) => new ManualStructureHostTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires floor selection
            exclusive: true,
            icon: 'Column',
            name: 'manual structure',
            disabledTooltip: 'select a floor first',
          },
        },
        // Spacer before undo/redo
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Undo action
        {
          type: ToolbarElementType.ACTION,
          id: 'undo',
          icon: 'undo',
          tooltip: 'undo',
        },
        // Redo action
        {
          type: ToolbarElementType.ACTION,
          id: 'redo',
          icon: 'redo',
          tooltip: 'redo',
        },
      ],
      viewConfig: {
        views: [ViewModes.PERSPECTIVE_3D_RGB, ViewModes.XRAY_2D],
        defaultViewId: 'perspective_3d_rgb',
      },
    },
  ],
};
